import { Fragment, useEffect, useMemo, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Buildings, PlusCircle, Trash, X, UserPlus, Storefront, GearSix, SquaresFour, PencilSimple, CaretRight, MagnifyingGlass, UsersThree } from "@phosphor-icons/react";

const VENUE_TYPES = ["restaurant", "hotel", "bar", "qsr", "cafe", "campus", "spa", "reception", "lounge"];
const TIMEZONES = ["Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo", "Europe/London", "Europe/Paris",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Australia/Sydney", "UTC"];
const ROLE_BADGE = {
  admin: "bg-slate-900 text-white", gm: "bg-violet-50 text-violet-700 border border-violet-200",
  manager: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  supervisor: "bg-amber-50 text-amber-700 border border-amber-200", staff: "bg-slate-100 text-slate-600",
};
const GROUPS = [
  { rank: 0, label: "Master Admins" },
  { rank: 1, label: "General Managers" },
  { rank: 2, label: "Cluster, Regional & Department Managers" },
  { rank: 3, label: "Line Staff / Team Members" },
];

export default function VenuesPage() {
  const { user } = useAuth();
  const [venues, setVenues] = useState([]);
  const [team, setTeam] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [rolesInfo, setRolesInfo] = useState({ roles: [], grantable: [], in_use: [], brand_roles: [] });
  const [hier, setHier] = useState({ tiers: [], nodes: [], venues: [] });
  const [settings, setSettings] = useState(null);
  const [selVenue, setSelVenue] = useState(null);
  const [selDept, setSelDept] = useState(null);
  const [venueModal, setVenueModal] = useState(null);
  const [vForm, setVForm] = useState({ name: "", type: "restaurant" });
  const [memberModal, setMemberModal] = useState(null);
  const [mf, setMf] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newDept, setNewDept] = useState("");
  const [newDeptVenue, setNewDeptVenue] = useState("");
  const [editDept, setEditDept] = useState(null);
  const [accessMember, setAccessMember] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [ownerTitle, setOwnerTitle] = useState("");
  const PERM_LABELS = [["read", "Read"], ["write", "Write"], ["approve", "Approve"], ["publish", "Publish"], ["broadcast", "Broadcast"], ["roster_edit", "Roster Edit"]];

  const isTopAdmin = ["admin", "gm"].includes(user.role);
  const canManageDepts = isTopAdmin || (user.role === "manager" && !(user.department_ids || []).length);

  const load = () => {
    api.get("/venues").then((r) => setVenues(r.data));
    api.get("/team").then((r) => setTeam(r.data));
    api.get("/team/roles").then((r) => setRolesInfo(r.data)).catch(() => {});
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/org/settings").then((r) => { setSettings(r.data); setOwnerTitle(r.data.owner_title || ""); }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const roleMeta = (role) => rolesInfo.roles.find((r) => r.value === role) || { value: role, label: role, rank: 3 };
  const roleLabelFor = (m) => (m.role_id && rolesInfo.brand_roles.find((r) => r.id === m.role_id)?.label) || (m.role === "admin" ? (settings?.owner_title || "Master Admin") : (rolesInfo.brand_roles.find((r) => r.system && r.base === m.role)?.label || roleMeta(m.role).label));
  const roleLabelOf = roleLabelFor;
  useEffect(() => { api.get("/hierarchy").then((r) => setHier(r.data)).catch(() => {}); }, []);
  const roleChoices = rolesInfo.brand_roles.filter((r) => rolesInfo.grantable.includes(r.base));
  const toggleIn = (key, id) => setMf({ ...mf, [key]: mf[key].includes(id) ? mf[key].filter((x) => x !== id) : [...mf[key], id] });
  const memberVenueIds = (m) => (m.venue_ids?.length ? m.venue_ids : (m.venue_id ? [m.venue_id] : []));
  const venueName = (id) => venues.find((v) => v.id === id)?.name;
  const deptName = (id) => departments.find((d) => d.id === id)?.name;
  const venueDisplay = (m) => {
    if (["cluster", "regional"].includes(m.role)) {
      if (m.full_access) return "Full access (all outlets)";
      const nodeNames = [...(m.assigned_clusters || []), ...(m.assigned_regions || [])].map((id) => hier.nodes.find((n) => n.id === id)?.name).filter(Boolean);
      const vnames = memberVenueIds(m).map(venueName).filter(Boolean);
      return [...nodeNames, ...vnames].join(", ") || "No scope assigned";
    }
    const names = memberVenueIds(m).map(venueName).filter(Boolean);
    if (names.length) return names.join(", ");
    const dnames = (m.department_ids || []).map(deptName).filter(Boolean);
    return dnames.length ? dnames.join(", ") : "Brand-wide";
  };

  /* ----- venues ----- */
  const openVenueModal = (venue) => {
    setVForm(venue ? { name: venue.name, type: venue.type, timezone: venue.timezone || "" } : { name: "", type: "restaurant", timezone: "" });
    setVenueModal(venue ? { mode: "edit", id: venue.id } : { mode: "create" });
  };
  const saveVenue = async () => {
    if (!vForm.name.trim()) return toast.error("Venue name is required");
    setBusy(true);
    try {
      if (venueModal.mode === "edit") {
        await api.put(`/venues/${venueModal.id}`, vForm);
        toast.success("Venue updated");
      } else {
        await api.post("/venues", vForm);
        toast.success("Venue added");
      }
      setVenueModal(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const deleteVenue = async (id) => {
    if (!window.confirm("Delete this venue? Its departments become property-wide.")) return;
    try {
      await api.delete(`/venues/${id}`);
      toast.success("Venue deleted");
      if (selVenue === id) { setSelVenue(null); setSelDept(null); }
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  /* ----- departments ----- */
  const addDept = async () => {
    if (!newDept.trim()) return toast.error("Department name is required");
    try {
      await api.post("/departments", { name: newDept, venue_id: newDeptVenue || null });
      toast.success("Department created");
      setNewDept(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveDept = async (id, patch) => {
    try { await api.put(`/departments/${id}`, patch); setEditDept(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deleteDept = async (id) => {
    if (!window.confirm("Delete this department permanently?")) return;
    try {
      await api.delete(`/departments/${id}`);
      toast.success("Department deleted");
      if (selDept === id) setSelDept(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  /* ----- members ----- */
  const openMemberModal = (member) => {
    if (member) {
      setMf({
        name: member.name, username: member.username || "", position: member.position || "",
        email: member.email?.endsWith("@practitality.local") ? "" : (member.email || ""),
        password: "", role: member.role, role_id: member.role_id || "", venue_ids: memberVenueIds(member),
        department_ids: member.department_ids || [], assigned_regions: member.assigned_regions || [],
        assigned_clusters: member.assigned_clusters || [], full_access: !!member.full_access,
      });
      setMemberModal({ mode: "edit", id: member.id });
    } else {
      const grantable = rolesInfo.grantable;
      setMf({ name: "", username: "", email: "", password: "", position: "",
              role: grantable.includes("staff") ? "staff" : (grantable[0] || "staff"), role_id: "",
              venue_ids: selVenue ? [selVenue] : [], department_ids: selDept ? [selDept] : [],
              assigned_regions: [], assigned_clusters: [], full_access: false });
      setMemberModal({ mode: "create" });
    }
  };
  const mfRank = mf ? roleMeta(mf.role).rank : 3;
  const mfDeptOptions = useMemo(() => {
    if (!mf) return [];
    return departments.filter((d) => d.active !== false &&
      (mfRank <= 1 || !d.venue_id || mf.venue_ids.includes(d.venue_id)));
  }, [mf, departments, mfRank]);

  const saveMember = async () => {
    if (!mf.name.trim()) return toast.error("Full name is required");
    const isAreaRole = ["cluster", "regional"].includes(mf.role);
    if (isAreaRole && !mf.full_access && mf.venue_ids.length + mf.assigned_regions.length + mf.assigned_clusters.length === 0) return toast.error("Assign at least one venue, region or cluster (or grant full access)");
    if (!isAreaRole && mfRank >= 2 && mf.venue_ids.length === 0) return toast.error("Select a venue for this role");
    if (!isAreaRole && mfRank === 2 && mf.department_ids.length === 0) return toast.error("Department Managers need at least one department");
    if (mfRank === 3 && mf.department_ids.length !== 1) return toast.error("Staff must have exactly one department");
    setBusy(true);
    try {
      if (memberModal.mode === "edit") {
        await api.put(`/team/${memberModal.id}`, {
          name: mf.name, email: mf.email, position: mf.position, role: mf.role, role_id: mf.role_id || null,
          venue_ids: mf.venue_ids, department_ids: mf.department_ids, assigned_regions: mf.assigned_regions,
          assigned_clusters: mf.assigned_clusters, full_access: mf.full_access,
        });
        toast.success("Team member updated");
      } else {
        if (mf.password.length < 6) return toast.error("Password must be 6+ characters");
        await api.post("/team", { ...mf, role_id: mf.role_id || null, position: mf.position || roleLabelOf(mf) });
        toast.success("Team member added");
      }
      setMemberModal(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const deleteMember = async (id) => {
    if (!window.confirm("Remove this team member permanently?")) return;
    try { await api.delete(`/team/${id}`); toast.success("Member removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveAccess = async (patch) => {
    try {
      const { data } = await api.put(`/team/${accessMember.id}/access`, patch);
      setAccessMember(data); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveDelegation = async () => {
    try {
      const { data } = await api.put(`/team/${accessMember.id}`, { can_allocate_handover_tasks: !accessMember.can_allocate_handover_tasks });
      setAccessMember(data); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  /* ----- settings ----- */
  const saveOwnerTitle = async () => {
    if (!ownerTitle.trim()) return toast.error("Enter a title (e.g. General Manager)");
    try {
      await api.put("/org/settings", { ...settings, owner_title: ownerTitle.trim() });
      setSettings({ ...settings, owner_title: ownerTitle.trim() });
      toast.success(`Account owner title set to "${ownerTitle.trim()}"`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggleManagerContent = async () => {
    const next = { ...settings, allow_manager_content: !settings.allow_manager_content };
    try {
      await api.put("/org/settings", next);
      setSettings(next);
      toast.success(next.allow_manager_content ? "Managers can now edit brand content" : "Brand content locked to Founder");
    } catch (e) { toast.error(apiError(e)); }
  };
  const updateTimezone = async (tz) => {
    const next = { ...settings, timezone: tz };
    try {
      await api.put("/org/settings", next);
      setSettings(next);
      toast.success(`Venue timezone set to ${tz} — daily shifts now follow local time`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const canEditMember = (m) => {
    if (m.id === user.id) return false;
    if (user.role === "admin") return true;
    if (user.role === "gm") return roleMeta(m.role).rank > 1;
    if (user.role === "manager") return m.role === "staff";
    return false;
  };

  /* ----- derived ----- */
  const drillDepts = departments.filter((d) => !selVenue || d.venue_id === selVenue || !d.venue_id);
  const deptMembers = selDept ? team.filter((m) => (m.department_ids || []).includes(selDept)) : [];
  const filteredTeam = team.filter((m) =>
    (!roleFilter || m.role === roleFilter) &&
    (!search || m.name.toLowerCase().includes(search.toLowerCase()) ||
      (m.email || "").toLowerCase().includes(search.toLowerCase()) ||
      (m.position || "").toLowerCase().includes(search.toLowerCase())));
  const grouped = GROUPS.map((g) => ({ ...g, members: filteredTeam.filter((m) => roleMeta(m.role).rank === g.rank) }))
    .filter((g) => g.members.length > 0);
  const roleOptions = rolesInfo.roles.filter((r) => rolesInfo.in_use.includes(r.value));

  return (
    <div className="space-y-10 rise-in" data-testid="venues-page">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400">Organization & Venue Management</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2">Venues & Team</h1>
        <p className="text-slate-500 mt-2 text-sm">Brand → Venues → Departments → Team. Click a venue to drill into its departments and people.</p>
      </div>

      {/* ---------- VENUES ---------- */}
      <section>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Buildings size={24} weight="duotone" /> Venues</h2>
          {isTopAdmin && (
            <button data-testid="add-venue-button" onClick={() => openVenueModal(null)}
              className="h-11 px-5 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center gap-2 tap-press hover:bg-slate-800">
              <PlusCircle size={18} /> Add venue
            </button>
          )}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="venues-list">
          {venues.map((v) => (
            <button key={v.id} data-testid={`venue-card-${v.id}`} type="button"
              onClick={() => { setSelVenue(selVenue === v.id ? null : v.id); setSelDept(null); }}
              className={`text-left bg-white border rounded-2xl p-6 card-lift border-l-4 tap-press ${selVenue === v.id
                ? "border-emerald-500 border-l-emerald-600 ring-2 ring-emerald-100" : "border-slate-200 border-l-emerald-500"}`}>
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Storefront size={22} weight="duotone" className="text-slate-700" />
                </div>
                {isTopAdmin && (
                  <span className="flex">
                    <span data-testid={`venue-edit-${v.id}`} role="button" tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); openVenueModal(v); }}
                      className="p-2 text-slate-300 hover:text-slate-700 tap-press cursor-pointer"><PencilSimple size={18} /></span>
                    <span data-testid={`delete-venue-${v.id}`} role="button" tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); deleteVenue(v.id); }}
                      className="p-2 text-slate-300 hover:text-red-500 tap-press cursor-pointer"><Trash size={18} /></span>
                  </span>
                )}
              </div>
              <p className="font-heading font-bold text-lg mt-3 leading-snug">{v.name}</p>
              <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mt-1">{v.type}</p>
              <p className="text-sm text-slate-500 mt-3">{v.department_count || 0} department{v.department_count !== 1 ? "s" : ""} · {v.staff_count} member{v.staff_count !== 1 ? "s" : ""}</p>
              <p className={`text-xs font-bold mt-2 flex items-center gap-1 ${selVenue === v.id ? "text-emerald-600" : "text-slate-400"}`}>
                {selVenue === v.id ? "Viewing departments below" : "View departments"} <CaretRight size={12} />
              </p>
            </button>
          ))}
          {venues.length === 0 && <p className="text-slate-400 text-sm">No venues yet — add the first one.</p>}
        </div>
      </section>

      {/* ---------- DEPARTMENTS (drill level 2) ---------- */}
      <section data-testid="departments-section">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2">
            <SquaresFour size={24} weight="duotone" /> Departments
            <span className="text-sm font-semibold text-slate-400">— {selVenue ? venueName(selVenue) : "all venues & property-wide"}</span>
          </h2>
          {selVenue && (
            <button data-testid="clear-venue-selection" onClick={() => { setSelVenue(null); setSelDept(null); }}
              className="h-9 px-4 rounded-full border border-slate-200 text-xs font-bold text-slate-500 tap-press hover:border-slate-900">
              Show all
            </button>
          )}
        </div>
        {canManageDepts && (
          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            <input data-testid="dept-name-input" value={newDept} onChange={(e) => setNewDept(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addDept()}
              placeholder="New department (e.g. Spa & Wellness)" className="flex-1 h-12 px-4 rounded-xl border border-slate-200" />
            <select data-testid="dept-venue-select" value={newDeptVenue} onChange={(e) => setNewDeptVenue(e.target.value)}
              className="h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold text-sm">
              <option value="">Property-wide</option>
              {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <button data-testid="dept-add-button" onClick={addDept}
              className="h-12 px-6 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center justify-center gap-2 tap-press hover:bg-slate-800">
              <PlusCircle size={18} /> Add department
            </button>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="departments-list">
          {drillDepts.map((d) => (
            <div key={d.id} data-testid={`dept-card-${d.id}`}
              className={`bg-white border rounded-2xl p-5 border-l-4 ${selDept === d.id ? "border-sky-500 border-l-sky-600 ring-2 ring-sky-100"
                : d.active !== false ? "border-slate-200 border-l-sky-500" : "border-slate-200 border-l-slate-300 opacity-60"}`}>
              {editDept === d.id ? (
                <input data-testid={`dept-rename-input-${d.id}`} defaultValue={d.name} autoFocus
                  onKeyDown={(e) => e.key === "Enter" && saveDept(d.id, { name: e.target.value })}
                  onBlur={(e) => saveDept(d.id, { name: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 font-bold text-sm" />
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <button data-testid={`drill-dept-${d.id}`} onClick={() => setSelDept(selDept === d.id ? null : d.id)}
                    className="font-heading font-bold leading-snug text-left tap-press hover:text-sky-700">{d.name}</button>
                  {canManageDepts && (
                    <span className="flex shrink-0">
                      <button data-testid={`dept-rename-btn-${d.id}`} onClick={() => setEditDept(d.id)}
                        className="p-1.5 text-slate-300 hover:text-slate-700 tap-press"><PencilSimple size={16} /></button>
                      <button data-testid={`dept-delete-${d.id}`} onClick={() => deleteDept(d.id)}
                        className="p-1.5 text-slate-300 hover:text-red-500 tap-press"><Trash size={16} /></button>
                    </span>
                  )}
                </div>
              )}
              <p className="text-xs text-slate-400 mt-1">{d.venue_id ? venueName(d.venue_id) || "—" : "Property-wide"} · {d.member_count} member{d.member_count !== 1 ? "s" : ""}</p>
              <div className="flex items-center justify-between mt-4">
                <button data-testid={`dept-view-team-${d.id}`} onClick={() => setSelDept(selDept === d.id ? null : d.id)}
                  className="text-xs font-bold text-sky-600 flex items-center gap-1 tap-press">
                  {selDept === d.id ? "Hide team" : "View team"} <CaretRight size={12} />
                </button>
                {canManageDepts && (
                  <button data-testid={`dept-active-toggle-${d.id}`} onClick={() => saveDept(d.id, { active: d.active === false })}
                    className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${d.active !== false ? "bg-emerald-500" : "bg-slate-300"}`}>
                    <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${d.active !== false ? "left-6" : "left-1"}`} />
                  </button>
                )}
              </div>
            </div>
          ))}
          {drillDepts.length === 0 && <p className="text-slate-400 text-sm">No departments here yet — add one above.</p>}
        </div>

        {/* ---------- TEAM IN DEPARTMENT (drill level 3) ---------- */}
        {selDept && (
          <div className="mt-6 bg-sky-50/50 border border-sky-100 rounded-2xl p-5 sm:p-6" data-testid="drill-team-panel">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-700 mb-4 flex items-center gap-1.5">
              <UsersThree size={16} /> Team — {deptName(selDept)}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {deptMembers.map((m) => (
                <div key={m.id} data-testid={`drill-member-${m.id}`}
                  className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-sm truncate">{m.name}</p>
                    <p className="text-xs text-slate-400 truncate">{m.position || roleLabelFor(m)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${ROLE_BADGE[m.role] || ROLE_BADGE.staff}`}>{roleLabelFor(m)}</span>
                    {canEditMember(m) && (
                      <button data-testid={`drill-member-edit-${m.id}`} onClick={() => openMemberModal(m)}
                        className="p-1.5 text-slate-300 hover:text-slate-700 tap-press"><PencilSimple size={16} /></button>
                    )}
                  </div>
                </div>
              ))}
              {deptMembers.length === 0 && <p className="text-slate-400 text-sm">No team members assigned to this department yet.</p>}
            </div>
          </div>
        )}
      </section>

      {/* ---------- MASTER TEAM DIRECTORY ---------- */}
      <section data-testid="team-directory">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2"><UserPlus size={24} weight="duotone" /> Master Team Directory</h2>
          {["admin", "gm", "manager"].includes(user.role) && (
            <button data-testid="add-member-button" onClick={() => openMemberModal(null)}
              className="h-11 px-5 rounded-full border-2 border-slate-200 text-sm font-bold flex items-center gap-2 tap-press hover:border-slate-900">
              <PlusCircle size={18} /> Add member
            </button>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="team-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email or job title…" className="w-full h-11 pl-11 pr-4 rounded-xl border border-slate-200 text-sm" />
          </div>
          <select data-testid="team-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
            className="h-11 px-4 rounded-xl border border-slate-200 bg-white font-semibold text-sm">
            <option value="">All roles</option>
            {roleOptions.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto" data-testid="team-list">
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider font-bold text-slate-400">
                <th className="px-6 py-4">Member</th><th className="px-4 py-4">Role</th>
                <th className="px-4 py-4">Job title</th><th className="px-4 py-4">Departments</th><th className="px-4 py-4">Venue</th><th className="px-4 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((g) => (
                <Fragment key={`grp-${g.rank}`}>
                  <tr data-testid={`dir-group-${g.rank}`} className="bg-slate-50/80">
                    <td colSpan={6} className="px-6 py-2.5 text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">{g.label} · {g.members.length}</td>
                  </tr>
                  {g.members.map((m) => (
                    <tr key={m.id} data-testid={`team-row-${m.id}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-6 py-4">
                        <p className="font-bold text-sm">{m.name}{m.id === user.id && <span className="text-slate-400 font-normal"> (you)</span>}</p>
                        <p className="text-xs text-slate-400">{m.email?.endsWith("@practitality.local") ? `@${m.username || ""}` : m.email}</p>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap ${ROLE_BADGE[m.role] || ROLE_BADGE.staff}`}>{roleLabelFor(m)}</span>
                      </td>
                      <td className="px-4 py-4 text-sm font-semibold text-slate-600">{m.position || "—"}</td>
                      <td className="px-4 py-4">
                        {(m.department_ids || []).length > 0 ? (
                          <span className="flex items-center gap-1.5 flex-wrap" data-testid={`member-dept-chips-${m.id}`}>
                            {(m.department_ids || []).map((id, i) => deptName(id) && (
                              <span key={`${id}-${i}`} className="text-[10px] font-bold uppercase tracking-wider bg-sky-50 text-sky-700 border border-sky-200 px-2 py-0.5 rounded-full">{deptName(id)}</span>
                            ))}
                          </span>
                        ) : <span className="text-xs font-semibold text-slate-400">All departments</span>}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-500" data-testid={`member-venue-${m.id}`}>{venueDisplay(m)}</td>
                      <td className="px-4 py-4 text-right whitespace-nowrap">
                        {canEditMember(m) && (
                          <button data-testid={`member-edit-${m.id}`} onClick={() => openMemberModal(m)}
                            className="text-xs font-bold text-slate-500 border border-slate-200 rounded-full px-3 py-1.5 tap-press hover:border-slate-900 mr-2">
                            Edit
                          </button>
                        )}
                        {(user.role === "admin" || user.is_master_operator) && m.id !== user.id && m.role !== "admin" && (
                          <button data-testid={`access-member-${m.id}`} onClick={() => setAccessMember(m)}
                            className="text-xs font-bold text-slate-500 border border-slate-200 rounded-full px-3 py-1.5 tap-press hover:border-slate-900 mr-2">
                            Access
                          </button>
                        )}
                        {(user.role === "admin" || (user.role === "gm" && roleMeta(m.role).rank > 1)) && m.id !== user.id && (
                          <button data-testid={`delete-member-${m.id}`} onClick={() => deleteMember(m.id)}
                            className="p-2 text-slate-300 hover:text-red-500 tap-press"><Trash size={18} /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
          {grouped.length === 0 && <p className="text-slate-400 p-6 text-sm">No team members match your search.</p>}
        </div>
      </section>

      {/* ---------- SYSTEM SETTINGS ---------- */}
      {user.role === "admin" && settings && (
        <section className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="system-settings">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2"><GearSix size={24} weight="duotone" /> System Settings</h2>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mt-5 gap-4">
            <div>
              <p className="font-bold text-sm">Account owner title</p>
              <p className="text-xs text-slate-500 mt-1">Shown across dashboards and user cards instead of "Owner" (e.g. General Manager, Managing Director, VP of Operations).</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <input data-testid="settings-owner-title-input" value={ownerTitle} onChange={(e) => setOwnerTitle(e.target.value)}
                placeholder="e.g. General Manager" className="h-12 px-4 rounded-xl border border-slate-200 text-sm w-52" />
              <button data-testid="settings-owner-title-save" onClick={saveOwnerTitle}
                className="h-12 px-5 rounded-full bg-slate-900 text-white text-xs font-bold tap-press hover:bg-slate-800">Save</button>
            </div>
          </div>
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-100 gap-4">
            <div>
              <p className="font-bold text-sm">Allow managers to edit brand content</p>
              <p className="text-xs text-slate-500 mt-1">When off, only the Founder can create SOPs, run Voice-to-SOP, and publish master training videos.</p>
            </div>
            <button data-testid="settings-manager-content-toggle" onClick={toggleManagerContent}
              className={`relative h-8 w-14 shrink-0 rounded-full transition-colors ${settings.allow_manager_content ? "bg-emerald-500" : "bg-slate-300"}`}>
              <span className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-all ${settings.allow_manager_content ? "left-7" : "left-1"}`} />
            </button>
          </div>
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-100 gap-4">
            <div>
              <p className="font-bold text-sm">Auto micro-checks</p>
              <p className="text-xs text-slate-500 mt-1">Auto-generate briefing micro-check questions from SOP quizzes and guest reviews. Managers can still edit them in My Briefing.</p>
            </div>
            <button data-testid="settings-micro-checks-toggle"
              onClick={async () => {
                const next = { ...settings, auto_micro_checks: settings.auto_micro_checks === false };
                try { await api.put("/org/settings", next); setSettings(next); toast.success(next.auto_micro_checks ? "Auto micro-checks enabled" : "Auto micro-checks disabled"); }
                catch (e) { toast.error(apiError(e)); }
              }}
              className={`relative h-8 w-14 shrink-0 rounded-full transition-colors ${settings.auto_micro_checks !== false ? "bg-emerald-500" : "bg-slate-300"}`}>
              <span className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-all ${settings.auto_micro_checks !== false ? "left-7" : "left-1"}`} />
            </button>
          </div>
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-100 gap-4">
            <div>
              <p className="font-bold text-sm">Venue timezone</p>
              <p className="text-xs text-slate-500 mt-1">Daily briefings, tasks and attendance reset at midnight in this timezone.</p>
            </div>
            <select data-testid="settings-timezone-select" value={settings.timezone || "Asia/Kolkata"}
              onChange={(e) => updateTimezone(e.target.value)}
              className="h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold text-sm shrink-0">
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
        </section>
      )}

      {/* ---------- VENUE MODAL ---------- */}
      {venueModal && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setVenueModal(null)}>
          <div data-testid="venue-modal" onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-md max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 rise-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold">{venueModal.mode === "edit" ? "Edit venue" : "Add venue"}</h2>
              <button onClick={() => setVenueModal(null)} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={24} /></button>
            </div>
            <div className="space-y-4">
              <input data-testid="venue-name-input" value={vForm.name} onChange={(e) => setVForm({ ...vForm, name: e.target.value })}
                placeholder="Venue name (e.g. Lobby Bar, Reception, Rooftop Café)" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              <select data-testid="venue-type-select" value={vForm.type} onChange={(e) => setVForm({ ...vForm, type: e.target.value })}
                className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold capitalize">
                {VENUE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <div>
                <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">Venue timezone</label>
                <input data-testid="venue-timezone-input" list="tz-options" value={vForm.timezone || ""} onChange={(e) => setVForm({ ...vForm, timezone: e.target.value })}
                  placeholder="Blank = brand default (e.g. Asia/Kolkata)" className="w-full h-12 px-4 rounded-xl border border-slate-200 text-sm mt-1" />
                <datalist id="tz-options">
                  {["Asia/Kolkata", "Asia/Dubai", "Asia/Riyadh", "Asia/Qatar", "Asia/Muscat", "Asia/Singapore", "Asia/Kuala_Lumpur", "Asia/Bangkok", "Asia/Kathmandu", "Asia/Colombo", "Asia/Dhaka", "Asia/Karachi", "Asia/Hong_Kong", "Asia/Tokyo", "Europe/London", "Europe/Paris", "Europe/Berlin", "America/New_York", "America/Chicago", "America/Los_Angeles", "Australia/Sydney", "Pacific/Auckland", "Africa/Nairobi", "Indian/Maldives"].map((z) => <option key={z} value={z} />)}
                </datalist>
                <p className="text-[11px] text-slate-400 mt-1">Drives the operating-day / shift-date rollover for this outlet's checklists.</p>
              </div>
              <button data-testid="venue-submit-button" onClick={saveVenue} disabled={busy}
                className="w-full h-14 rounded-full bg-slate-900 text-white font-heading font-bold tap-press hover:bg-slate-800 disabled:opacity-50">
                {busy ? "Saving…" : venueModal.mode === "edit" ? "Save changes" : "Add venue"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- MEMBER MODAL ---------- */}
      {memberModal && mf && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setMemberModal(null)}>
          <div data-testid="member-modal" onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-md max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 rise-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold">{memberModal.mode === "edit" ? "Edit team member" : "Add team member"}</h2>
              <button onClick={() => setMemberModal(null)} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={24} /></button>
            </div>
            <div className="space-y-4">
              <input data-testid="member-name-input" value={mf.name} onChange={(e) => setMf({ ...mf, name: e.target.value })}
                placeholder="Full name" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              {memberModal.mode === "create" && (
                <input data-testid="member-username-input" value={mf.username} onChange={(e) => setMf({ ...mf, username: e.target.value })}
                  placeholder="Username (login credential)" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              )}
              <input data-testid="member-email-input" type="email" value={mf.email} onChange={(e) => setMf({ ...mf, email: e.target.value })}
                placeholder="Email (optional, for alerts)" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              {memberModal.mode === "create" && (
                <input data-testid="member-password-input" type="password" value={mf.password} onChange={(e) => setMf({ ...mf, password: e.target.value })}
                  placeholder="Temporary password (6+ chars)" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              )}
              <div className="grid grid-cols-2 gap-4">
                <select data-testid="member-role-select" value={mf.role_id || `base:${mf.role}`}
                  onChange={(e) => { const val = e.target.value; const br = rolesInfo.brand_roles.find((r) => r.id === val);
                    setMf({ ...mf, role: br ? br.base : val.replace("base:", ""), role_id: br && !br.system ? br.id : "", department_ids: [], assigned_regions: [], assigned_clusters: [], full_access: false }); }}
                  className="h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold">
                  {roleChoices.map((r) => <option key={r.id} value={r.system ? `base:${r.base}` : r.id}>{r.label}</option>)}
                  {memberModal.mode === "edit" && !roleChoices.some((r) => (r.system ? r.base === mf.role && !mf.role_id : r.id === mf.role_id)) && <option value={mf.role_id || `base:${mf.role}`}>{roleLabelOf(mf)}</option>}
                </select>
                <input data-testid="member-position-input" value={mf.position} onChange={(e) => setMf({ ...mf, position: e.target.value })}
                  placeholder="Custom job title" className="h-12 px-4 rounded-xl border border-slate-200" />
              </div>

              {["cluster", "regional"].includes(mf.role) ? (
                <div className="space-y-3" data-testid="member-scope-picker">
                  {["admin", "gm"].includes(user.role) && (
                    <label className="flex items-center gap-2 text-xs font-bold text-slate-600"><input data-testid="member-full-access" type="checkbox" checked={mf.full_access} onChange={(e) => setMf({ ...mf, full_access: e.target.checked })} className="h-4 w-4" /> Full access (every outlet, brand-wide)</label>
                  )}
                  {!mf.full_access && hier.tiers.length === 2 && (
                    <div><p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">{hier.tiers[0].label}s</p>
                      <div className="flex flex-wrap gap-2">{hier.nodes.filter((n) => n.tier === 0).map((n) => <button key={n.id} type="button" data-testid={`member-cluster-chip-${n.id}`} onClick={() => toggleIn("assigned_clusters", n.id)} className={`h-9 px-4 rounded-full text-xs font-bold tap-press border ${mf.assigned_clusters.includes(n.id) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>{n.name}</button>)}</div></div>
                  )}
                  {!mf.full_access && (
                    <div><p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">{hier.tiers[hier.tiers.length - 1]?.label || "Region"}s</p>
                      <div className="flex flex-wrap gap-2">{hier.nodes.filter((n) => n.tier === hier.tiers.length - 1).map((n) => <button key={n.id} type="button" data-testid={`member-region-chip-${n.id}`} onClick={() => toggleIn("assigned_regions", n.id)} className={`h-9 px-4 rounded-full text-xs font-bold tap-press border ${mf.assigned_regions.includes(n.id) ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>{n.name}</button>)}
                      {hier.nodes.length === 0 && <p className="text-xs text-slate-400">No groups yet — create them under Organisation, or pick venues directly.</p>}</div></div>
                  )}
                  {!mf.full_access && (
                    <div><p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">Individual venues</p>
                      <div className="flex flex-wrap gap-2">{venues.map((vv) => <button key={vv.id} type="button" data-testid={`member-venue-chip-${vv.id}`} onClick={() => toggleIn("venue_ids", vv.id)} className={`h-9 px-4 rounded-full text-xs font-bold tap-press border ${mf.venue_ids.includes(vv.id) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>{vv.name}</button>)}</div></div>
                  )}
                </div>
              ) : mfRank <= 1 ? (
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">Venue scope (GM can be brand-wide or specific venues)</p>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" data-testid="member-venue-brandwide"
                      onClick={() => setMf({ ...mf, venue_ids: [] })}
                      className={`h-9 px-4 rounded-full text-xs font-bold tap-press border ${mf.venue_ids.length === 0
                        ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>
                      Brand-wide (all venues)
                    </button>
                    {venues.map((v) => (
                      <button key={v.id} type="button" data-testid={`member-venue-chip-${v.id}`}
                        onClick={() => setMf({ ...mf, venue_ids: mf.venue_ids.includes(v.id) ? mf.venue_ids.filter((x) => x !== v.id) : [...mf.venue_ids, v.id] })}
                        className={`h-9 px-4 rounded-full text-xs font-bold tap-press border ${mf.venue_ids.includes(v.id)
                          ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>
                        {v.name}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <select data-testid="member-venue-select" value={mf.venue_ids[0] || ""}
                  onChange={(e) => setMf({ ...mf, venue_ids: e.target.value ? [e.target.value] : [], department_ids: [] })}
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold">
                  <option value="">Select venue (required)</option>
                  {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              )}

              {["cluster", "regional"].includes(mf.role) ? null : mfRank === 3 ? (
                <select data-testid="member-dept-select" value={mf.department_ids[0] || ""}
                  onChange={(e) => setMf({ ...mf, department_ids: e.target.value ? [e.target.value] : [] })}
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold">
                  <option value="">Select primary department (required)</option>
                  {mfDeptOptions.map((d) => <option key={d.id} value={d.id}>{d.name}{!d.venue_id ? " (property-wide)" : ""}</option>)}
                </select>
              ) : (
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">
                    {mfRank === 2 ? "Departments managed (at least one)" : "Departments (blank = all departments)"}
                  </p>
                  <div className="flex flex-wrap gap-2" data-testid="member-dept-picker">
                    {mfDeptOptions.map((d) => (
                      <button key={d.id} type="button" data-testid={`member-dept-chip-${d.id}`}
                        onClick={() => setMf({ ...mf, department_ids: mf.department_ids.includes(d.id) ? mf.department_ids.filter((x) => x !== d.id) : [...mf.department_ids, d.id] })}
                        className={`h-9 px-4 rounded-full text-xs font-bold tap-press border ${mf.department_ids.includes(d.id)
                          ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>
                        {d.name}
                      </button>
                    ))}
                    {mfDeptOptions.length === 0 && <p className="text-xs text-slate-400">No departments for the selected venue — add one first.</p>}
                  </div>
                </div>
              )}

              <button data-testid="member-submit-button" onClick={saveMember} disabled={busy}
                className="w-full h-14 rounded-full bg-slate-900 text-white font-heading font-bold tap-press hover:bg-slate-800 disabled:opacity-50">
                {busy ? "Saving…" : memberModal.mode === "edit" ? "Save changes" : "Add member"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- ACCESS MODAL ---------- */}
      {accessMember && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setAccessMember(null)}>
          <div data-testid="access-control-modal" onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-md max-h-[90vh] overflow-y-auto rounded-3xl p-6 sm:p-8 rise-in">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-heading text-2xl font-bold">Access control</h2>
              <button onClick={() => setAccessMember(null)} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={22} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-5">{accessMember.name} · {accessMember.position || accessMember.role}</p>
            {user.role === "admin" && (
              <div className="flex items-center justify-between gap-3 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 mb-4">
                <div>
                  <p className="text-sm font-bold text-violet-800">Master Operator</p>
                  <p className="text-[11px] text-violet-600">Full administrative authority delegated by the GM</p>
                </div>
                <button data-testid="master-operator-toggle"
                  onClick={() => saveAccess({ is_master_operator: !accessMember.is_master_operator })}
                  className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${accessMember.is_master_operator ? "bg-violet-500" : "bg-slate-300"}`}>
                  <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${accessMember.is_master_operator ? "left-6" : "left-1"}`} />
                </button>
              </div>
            )}
            {["staff", "supervisor"].includes(accessMember.role) && (
              <div className="flex items-center justify-between gap-3 bg-sky-50 border border-sky-200 rounded-xl px-4 py-3 mb-4">
                <div>
                  <p className="text-sm font-bold text-sky-800">Handover task delegation</p>
                  <p className="text-[11px] text-sky-600">Can allocate tasks &amp; assignees inside shift handovers</p>
                </div>
                <button data-testid="delegation-access-toggle"
                  onClick={saveDelegation}
                  className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${accessMember.can_allocate_handover_tasks ? "bg-sky-500" : "bg-slate-300"}`}>
                  <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${accessMember.can_allocate_handover_tasks ? "left-6" : "left-1"}`} />
                </button>
              </div>
            )}
            <div className="space-y-2">
              {PERM_LABELS.map(([key, label]) => {
                const defaults = { gm: ["read", "write", "approve", "publish", "broadcast", "roster_edit"], manager: ["read", "write", "approve", "publish", "broadcast", "roster_edit"], supervisor: ["read", "write"], staff: ["read", "write"] };
                const overrides = accessMember.permissions || {};
                const val = key in overrides ? overrides[key] : (defaults[accessMember.role] || []).includes(key);
                return (
                  <div key={key} className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
                    <p className="text-sm font-bold">{label}</p>
                    <button data-testid={`perm-toggle-${key}`}
                      onClick={() => saveAccess({ permissions: { ...overrides, [key]: !val } })}
                      className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${val ? "bg-emerald-500" : "bg-slate-300"}`}>
                      <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${val ? "left-6" : "left-1"}`} />
                    </button>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400 mt-4">Master Operators bypass individual permissions. Changes apply on the member's next request.</p>
          </div>
        </div>
      )}
    </div>
  );
}
